/**
 * studentRecord — every rule the admin student page applies to its data.
 *
 * Pure: no fetching, no React. Which enrolments count toward the fee balance,
 * when an installment is overdue, when attendance or a quiet login needs a
 * person — each is decided here once, so the header, the vitals, the attention
 * band and the tabs cannot disagree about it.
 */
import { formatCurrency, formatDate, formatTimeOfDay, DASH } from "../../../utils/format";

const DAY_MS = 86400000;
const IST_DAY = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Kolkata" });

/** "YYYY-MM-DD" on the IST calendar — comparable as a string. */
export const istDayKey = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : IST_DAY.format(d);
};

const idOf = (ref) => (ref && typeof ref === "object" ? ref._id : ref) || null;
const sameId = (a, b) => !!a && !!b && String(a) === String(b);
const money = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/* ── Enrolments ───────────────────────────────────────────────────────────── */

const ENROLMENT_ORDER = { active: 0, completed: 1, transferred: 2, dropped: 3 };

/**
 * Every course the student is enrolled in, primary first.
 *
 * ⚠️ A student admitted before `CourseAssignment` existed has their primary
 * course only on `Student.course` / `batch`, with no assignment row. It is
 * folded in here the way the backend's `getStudentEnrollments` does, so its
 * fees are not silently dropped from the balance.
 */
export function listEnrollments(student, courseAssignments = []) {
  if (!student) return [];
  const primaryBatch = student.batch && typeof student.batch === "object" ? student.batch : null;

  const rows = courseAssignments.map((ca) => {
    const populated = ca.batchId && typeof ca.batchId === "object" ? ca.batchId : null;
    // The primary batch arrives fully populated on the student; the
    // assignment's copy carries only a subset of its fields.
    const batch = populated && primaryBatch && sameId(populated._id, primaryBatch._id)
      ? { ...populated, ...primaryBatch }
      : populated;
    return {
      key: String(ca._id),
      courseAssignment: ca,
      courseId: idOf(ca.courseId),
      courseName: ca.courseId?.name || "Course",
      batch,
      status: ca.status || "active",
      isPrimary: !!ca.isPrimary,
      joinedDate: ca.joinedDate || ca.createdAt || null,
      completedDate: ca.completedDate || null,
      totalFees: money(ca.totalFees),
      paidFees: money(ca.paidFees),
      pendingFees: money(ca.pendingFees),
      // This student's discount on this course, already taken off totalFees.
      // A row from before discounts has none.
      discount: ca.discount && money(ca.discount.amount) > 0 ? ca.discount : null,
    };
  });

  const primaryCourseId = idOf(student.course);
  if (primaryCourseId && !rows.some((r) => sameId(r.courseId, primaryCourseId))) {
    rows.unshift({
      key: "legacy-primary",
      courseAssignment: null,
      courseId: primaryCourseId,
      courseName: student.course?.name || "Course",
      batch: primaryBatch,
      status: "active",
      isPrimary: true,
      joinedDate: student.joinedDate || student.createdAt || null,
      completedDate: null,
      totalFees: money(student.totalFees),
      paidFees: money(student.paidFees),
      pendingFees: money(student.pendingFees),
      discount: null,
    });
  }

  return rows.sort((a, b) => (
    Number(b.isPrimary) - Number(a.isPrimary)
    || (ENROLMENT_ORDER[a.status] ?? 9) - (ENROLMENT_ORDER[b.status] ?? 9)
  ));
}

/** The enrolment whose batch is in a live class right now, if any. */
export const liveEnrollment = (enrollments) =>
  enrollments.find((e) => e.status === "active" && e.batch?.isLive) || null;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** "Mon, Wed, Fri · 10:00 am – 12:00 pm", or as much of it as the batch says. */
export function batchSchedule(batch) {
  if (!batch) return "";
  const days = Array.isArray(batch.meetingDays) && batch.meetingDays.length
    ? (batch.meetingDays.length === 7
      ? "Daily"
      // Monday-first, the way a timetable is read.
      : [...batch.meetingDays].sort((a, b) => ((a + 6) % 7) - ((b + 6) % 7)).map((d) => WEEKDAYS[d]).join(", "))
    : ({ WEEKDAY: "Weekdays", WEEKEND: "Weekends" }[batch.batchType] || "");
  const start = formatTimeOfDay(batch.startTime);
  const end = formatTimeOfDay(batch.endTime);
  const time = start === DASH ? "" : `${start}${end === DASH ? "" : ` – ${end}`}`;
  return [days, time].filter(Boolean).join(" · ");
}

/** "3 Sep 2026 – 20 Dec 2026", or whichever end is known. */
export function batchDates(batch) {
  if (!batch) return "";
  const from = batch.startDate ? formatDate(batch.startDate) : "";
  const to = batch.endDate ? formatDate(batch.endDate) : "";
  if (from && to) return `${from} – ${to}`;
  if (from) return `From ${from}`;
  return to ? `Until ${to}` : "";
}

/* ── Fees ─────────────────────────────────────────────────────────────────── */

/**
 * The enrolments that count toward the headline balance. A dropped or
 * transferred course keeps its own figures on the Fees tab, but its pending
 * amount is not something the student is still expected to pay — the same
 * scope the backend uses for their own "pending fees".
 */
const COUNTED = new Set(["active", "completed"]);

export function summarizeFees(enrollments) {
  const counted = enrollments.filter((e) => COUNTED.has(e.status));
  const total = counted.reduce((s, e) => s + e.totalFees, 0);
  const paid = counted.reduce((s, e) => s + e.paidFees, 0);
  const pending = counted.reduce((s, e) => s + e.pendingFees, 0);
  // Already taken off `total`; said beside it so a lower figure is explained.
  const discount = counted.reduce((s, e) => s + money(e.discount?.amount), 0);
  return {
    total,
    paid,
    pending,
    discount,
    paidPct: total > 0 ? Math.min(100, Math.round((paid / total) * 100)) : null,
  };
}

const SETTLED = new Set(["paid", "waived"]);

export const installmentBalance = (inst) =>
  Math.max(money(inst?.amount) - money(inst?.paidAmount), 0);

/**
 * An installment's status, corrected for a due date that has passed since the
 * daily 09:30 IST job last refreshed the stored one. Without this, an EMI due
 * yesterday reads "Due" until tomorrow morning.
 */
export function installmentStatus(inst, todayKey = istDayKey(new Date())) {
  const stored = String(inst?.status || "upcoming").toLowerCase();
  if (SETTLED.has(stored)) return stored;
  const dueKey = istDayKey(inst?.dueDate);
  if (installmentBalance(inst) > 0 && dueKey && dueKey < todayKey) return "overdue";
  return stored;
}

/** Everything still owed on the student's installment plans, soonest first. */
export function summarizeInstallments(plans = [], now = new Date()) {
  const todayKey = istDayKey(now);
  const open = [];
  plans.forEach((plan) => {
    if (plan.status === "cancelled") return;
    (plan.installments || []).forEach((inst) => {
      const status = installmentStatus(inst, todayKey);
      const balance = installmentBalance(inst);
      if (SETTLED.has(status) || balance <= 0) return;
      open.push({
        planId: plan._id,
        seq: inst.seq,
        label: inst.label || `EMI ${inst.seq}`,
        dueDate: inst.dueDate,
        balance,
        status,
        courseName: plan.courseId?.name || "",
      });
    });
  });
  open.sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));
  const overdue = open.filter((i) => i.status === "overdue");
  return {
    overdueCount: overdue.length,
    overdueAmount: overdue.reduce((s, i) => s + i.balance, 0),
    oldestOverdue: overdue[0] || null,
    next: open.find((i) => i.status !== "overdue") || null,
  };
}

/** The most recent payment that actually brought money in. */
export const lastPayment = (payments = []) =>
  [...payments]
    .filter((p) => p.paymentStatus === "success" || p.paymentStatus === "partially_refunded")
    .sort((a, b) => new Date(b.date || b.createdAt) - new Date(a.date || a.createdAt))[0] || null;

/* ── Attendance ───────────────────────────────────────────────────────────── */

/** Used when the batch sets no minimum — the same default the attendance module uses. */
export const DEFAULT_MIN_ATTENDANCE = 75;

/**
 * Attendance against the primary batch's own minimum. Warning within 15 points
 * of it, danger below that. `held` is 0 until a class of 15+ minutes has ended,
 * and then there is nothing to judge.
 */
export function attendanceStanding(stats, student) {
  const att = stats?.attendance || {};
  const minPct = student?.batch?.minAttendancePct ?? DEFAULT_MIN_ATTENDANCE;
  const held = att.totalBatchSessions || 0;
  const base = { held, present: att.present || 0, partial: att.partial || 0, minPct };
  if (!held) return { ...base, percent: null, tone: "neutral" };
  const percent = att.percent ?? 0;
  const tone = percent >= minPct ? "success" : percent >= minPct - 15 ? "warning" : "danger";
  return { ...base, percent, tone };
}

/* ── Login and engagement ─────────────────────────────────────────────────── */

/** After this many days without activity, a student with a login needs a nudge. */
export const QUIET_AFTER_DAYS = 14;

/**
 * `active` · `invited` (added, never signed up) · `none` (never signed up and
 * no email to invite with) — or null when the API sent no account block.
 */
export function accountState(student) {
  const account = student?.account;
  if (!account) return null;
  if (account.hasLogin) return "active";
  return student.email ? "invited" : "none";
}

export function engagement(student, now = new Date()) {
  const account = student?.account;
  if (!account) return null;
  const last = account.lastActiveAt ? new Date(account.lastActiveAt) : null;
  const idleDays = last ? Math.max(0, Math.floor((now - last) / DAY_MS)) : null;
  const quiet = account.hasLogin && (idleDays === null || idleDays >= QUIET_AFTER_DAYS);
  return {
    hasLogin: !!account.hasLogin,
    lastActiveAt: last,
    idleDays,
    activeDays30: account.activeDays30 ?? 0,
    tone: !account.hasLogin ? "neutral" : quiet ? "warning" : "success",
  };
}

/* ── What needs a person ──────────────────────────────────────────────────── */

/**
 * The attention band, most urgent first. Each item names an `action` the page
 * wires to a handler; the item never knows how the action is carried out.
 *
 * Attendance waits for five classes: two missed out of three held is 33 %, and
 * flagging that in the first week is noise, not a signal.
 */
export function buildAttention({ student, installments, attendance, activity, feesEnabled }) {
  if (!student) return [];
  const items = [];

  if (feesEnabled && installments?.overdueCount) {
    const oldest = installments.oldestOverdue;
    items.push({
      key: "overdue",
      tone: "danger",
      title: `${formatCurrency(installments.overdueAmount)} overdue`,
      detail: installments.overdueCount === 1
        ? `${oldest.label}${oldest.courseName ? ` for ${oldest.courseName}` : ""} was due on ${formatDate(oldest.dueDate)}.`
        : `${installments.overdueCount} installments are past due; the oldest was due on ${formatDate(oldest.dueDate)}.`,
      action: "remind",
    });
  }

  if (attendance && attendance.held >= 5 && attendance.percent < attendance.minPct) {
    items.push({
      key: "attendance",
      tone: attendance.tone === "danger" ? "danger" : "warning",
      title: `Attendance is ${attendance.percent}%`,
      detail: `Below the ${attendance.minPct}% minimum — present in ${attendance.present} of ${attendance.held} classes.`,
      action: "attendance",
    });
  }

  // A student marked inactive is already somebody's decision; nagging about
  // their login would only add noise to a record nobody is chasing.
  if (student.status !== "inactive") {
    const state = accountState(student);
    if (state === "invited") {
      items.push({
        key: "invite",
        tone: "warning",
        title: "Has not signed up yet",
        detail: "Until they do, class links and app notifications do not reach them.",
        action: "resendWelcome",
      });
    } else if (state === "none") {
      items.push({
        key: "no-email",
        tone: "warning",
        title: "Has not signed up yet",
        detail: "There is no email on file, so a welcome email cannot be sent.",
        action: "edit",
      });
    } else if (activity?.hasLogin && activity.tone === "warning") {
      items.push({
        key: "quiet",
        tone: "warning",
        title: activity.idleDays === null ? "No recent activity" : `No activity for ${activity.idleDays} days`,
        detail: activity.lastActiveAt
          ? `Last active on ${formatDate(activity.lastActiveAt)}.`
          : "They have not opened the app recently.",
        action: "call",
      });
    }
  }

  return items;
}

/* ── Placement and performance ────────────────────────────────────────────── */

const lower = (v) => String(v || "").toLowerCase();

export function summarizeInterviews(interviews = []) {
  const count = (...states) => interviews.filter((i) => states.includes(lower(i.currentStatus))).length;
  return {
    total: interviews.length,
    inProgress: count("scheduled", "ongoing"),
    offers: count("offered", "joined"),
    joined: count("joined"),
  };
}

/** The next round still to happen across every drive, with its company. */
export function nextInterviewRound(interviews = [], now = new Date()) {
  const todayKey = istDayKey(now);
  const upcoming = [];
  interviews.forEach((iv) => {
    (iv.rounds || []).forEach((r) => {
      const state = lower(r.status);
      if (state !== "scheduled" && state !== "rescheduled") return;
      if (!r.date || r.date < todayKey) return;
      upcoming.push({ ...r, interviewId: iv._id, companyName: iv.companyName });
    });
  });
  upcoming.sort((a, b) => `${a.date} ${a.time || ""}`.localeCompare(`${b.date} ${b.time || ""}`));
  return upcoming[0] || null;
}

/**
 * A figure that was actually recorded. `Number(null)` is 0, so a bare
 * `Number.isFinite(Number(v))` would score an unmarked attempt as 0 %.
 */
export const isRecorded = (v) => v !== null && v !== undefined && v !== "" && Number.isFinite(Number(v));

/** Submitted assessment attempts, as the portfolio endpoint returns them. */
export function summarizeAssessments(quizzes = []) {
  const scored = quizzes.filter((q) => isRecorded(q.percentage));
  return {
    taken: quizzes.length,
    passed: quizzes.filter((q) => q.passed).length,
    avgPercent: scored.length
      ? Math.round(scored.reduce((s, q) => s + Number(q.percentage), 0) / scored.length)
      : null,
  };
}

/* ── Personal ─────────────────────────────────────────────────────────────── */

export function ageFrom(dob, now = new Date()) {
  const d = dob ? new Date(dob) : null;
  if (!d || Number.isNaN(d.getTime())) return null;
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age -= 1;
  return age >= 0 && age < 120 ? age : null;
}
